import {
  Box,
  Button,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { User, UserRoles } from "../../types";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Input } from "../../hookFormComponents.tsx/Input";
import { api } from "../../api/api";
import SelectM from "../../hookFormComponents.tsx/Select";
import { useEffect, useState } from "react";

type Props = {
  modalConfig: { open: boolean; data: any };
  handleClose: () => void;
  setModalConfig: React.Dispatch<
    React.SetStateAction<
      | {
          data?: User;
          open: boolean;
        }
      | undefined
    >
  >;
  fetcher: () => Promise<void>;
};

function CreateModal({
  modalConfig,
  handleClose,
  fetcher,
  setModalConfig,
}: Props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "white",
    boxShadow: 24,
    borderRadius: "10px",
    p: 4,
  };

  const validationSchema1 = Yup.object({
    name: Yup.string().required("Required"),
    price: Yup.number().typeError("Must be number").required("Required"),
    all_time_slots: Yup.number()
      .required("Required")
      .typeError("Must be number"),
    equipment: Yup.array(Yup.string()).min(1, "Must select at least 1"),
    employees: Yup.array(Yup.string()).min(1, "Must select at least 1"),
    spaces: Yup.array(Yup.string()).min(1, "Must select at least 1"),
    description: Yup.string(),
  });

  const validationSchema2 = Yup.object({
    name: Yup.string().required("Required"),
    price: Yup.number().typeError("Must be number").required("Required"),
    all_time_slots: Yup.number()
      .required("Required")
      .typeError("Must be number"),
    equipment: Yup.array(Yup.string()).min(1, "Must select at least 1"),
    employees: Yup.array(Yup.string()).min(1, "Must select at least 1"),
    spaces: Yup.array(Yup.string()).min(1, "Must select at least 1"),
    description: Yup.string(),
  });

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [spaceOptions, setSpaceOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);

  const getEmployees = () => {
    api.get("/users").then((res) =>
      setEmployeeOptions(
        res.data.users.map((employee: any) => ({
          value: employee.id,
          label: `${employee.first_name} ${employee.last_name}`,
        }))
      )
    );
  };
  const getEquipment = () => {
    api.get("/equipment").then((res) =>
      setEquipmentOptions(
        res.data.equipment.map((equipment: any) => ({
          value: equipment.id,
          label: equipment.name,
        }))
      )
    );
  };
  const getSpaces = () => {
    api.get("/spaces").then((res) =>
      setSpaceOptions(
        res.data.spaces.map((space: any) => ({
          value: space.id,
          label: space.name,
        }))
      )
    );
  };

  useEffect(() => {
    getEmployees();
    getEquipment();
    getSpaces();
  }, []);

  console.log(modalConfig.data);

  const {
    formState: { errors },
    control,
    setValue,
    register,
    handleSubmit,
    getValues,
  } = useForm<
    Yup.InferType<typeof validationSchema1 | typeof validationSchema2>
  >({
    resolver: yupResolver(
      !modalConfig.data ? validationSchema1 : validationSchema2
    ),
    defaultValues: modalConfig.data
      ? {
          name: modalConfig.data.name,
          price: modalConfig.data.price,
          all_time_slots: modalConfig.data.all_time_slots,
          equipment: modalConfig.data.equipment_ids
            ? modalConfig.data.equipment_ids.split(",")
            : [],
          employees: modalConfig.data.employee_ids
            ? modalConfig.data.employee_ids.split(",")
            : [],
          spaces: modalConfig.data.spaces_ids
            ? modalConfig.data.spaces_ids.split(",")
            : [],
          description: modalConfig.data.description,
        }
      : {},
  });

  return (
    <Modal open={modalConfig.open} onClose={handleClose}>
      <Box sx={style}>
        <form
          noValidate
          onSubmit={handleSubmit((values) => {
            if (modalConfig.data) {
              api
                .post(`/services/${modalConfig.data.id}/update`, values)
                .then((res) => {
                  fetcher();
                  setModalConfig((prev) => ({
                    data: undefined,
                    open: false,
                  }));
                })
                .catch((e) => console.log(e));
            } else {
              api
                .post(`/services/create`, values)
                .then((res) => {
                  fetcher();
                  setModalConfig((prev) => ({
                    data: undefined,
                    open: false,
                  }));
                })
                .catch((e) => console.log(e));
            }
          })}
        >
          <Input
            required
            {...register("name")}
            label={"Name"}
            type="text"
            error={errors?.name?.message}
          />
          <Input
            required
            {...register("price")}
            label={"Price"}
            type="text"
            error={errors?.price?.message}
          />
          <Input
            required
            {...register("description")}
            label={"Description"}
            type="text"
            error={errors?.description?.message}
          />

          <Controller
            control={control}
            name="employees"
            render={({ field }) => {
              return (
                <SelectM
                  label="Employees"
                  multiple
                  // @ts-ignore
                  value={
                    field.value ? field.value.map((item) => Number(item)) : []
                  }
                  name={field.name}
                  setValue={setValue}
                  errors={errors}
                  options={employeeOptions}
                />
              );
            }}
          />

          <Controller
            control={control}
            name="spaces"
            render={({ field }) => {
              return (
                <SelectM
                  label="Spaces"
                  required
                  multiple
                  // @ts-ignore
                  value={
                    field.value ? field.value.map((item) => Number(item)) : []
                  }
                  name={field.name}
                  setValue={setValue}
                  errors={errors}
                  options={spaceOptions}
                />
              );
            }}
          />

          <Controller
            control={control}
            name="equipment"
            render={({ field }) => {
              return (
                <SelectM
                  label="Equipment"
                  required
                  multiple
                  // @ts-ignore
                  value={
                    field.value ? field.value.map((item) => Number(item)) : []
                  }
                  name={field.name}
                  setValue={setValue}
                  errors={errors}
                  options={equipmentOptions}
                />
              );
            }}
          />

          <Controller
            control={control}
            name="all_time_slots"
            render={({ field }) => (
              <SelectM
                label="Available for all time slots"
                required
                value={
                  field.value || field.value === 0
                    ? field.value?.toString()
                    : ""
                }
                name={field.name}
                setValue={setValue}
                errors={errors}
                options={[
                  { label: "Yes", value: 1 },
                  { label: "No", value: 0 },
                ]}
              />
            )}
          />

          <Box sx={{ display: "flex", justifyContent: "right" }}>
            <Button type="submit" variant="contained">
              Submit
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}

export default CreateModal;
